import React, { useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { SplitButton } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { SafeSVG } from "../svg/SafeSVG";
import "./SplitButton.scss";

/**
 * Split button
 * @module
 * @param {string} text - Button text
 * @param {array} items - items
 * @param {string} [className] - ClassName of SplitButton
 * @param {string} [buttonClassName] - ButtonClassName
 * @param {string} [icon] - Icon name
 * @param {func} onClick - Function invoked on button click
 * @param {bool} [disabled] - Disabled button
 * @param {object} [popupSettings] - Popup settings
 * @param {string} [imageUrl] - Image URL
 * @param {string} [position] - Tooltip position, if not provided - top
 * @param {string} [tooltip] - Tooltip
 */
const SplitButtonComponent = (props) => {
  const propsItemRender = props.itemRender;
  const ref = useRef(null);
  const onClick = (e) => {
    if (e?.item?.disableClick) {
      return;
    }
    if (e.itemIndex !== undefined) {
      props.onClick(e.itemIndex, e.item.text);
    } else {
      props.onClick(-1, props.text);
    }
  };

  const itemRender = useCallback((itemProps) => {
    if (propsItemRender) {
      return propsItemRender(itemProps);
    }

    const title = itemProps.item.title ? itemProps.item.title : itemProps.item.text;
    return (
      <div className="drop-down-button-item" data-tooltip={title}>
        {itemProps.item.imageUrl ? (
          <SafeSVG className="drop-down-button-item-SVG" src={itemProps.item.imageUrl} />
        ) : (
          <span
            className={
              itemProps.item.iconClass
                ? itemProps.item.iconClass
                : `k-icon k-font-icon k-i-${itemProps.item.icon}`
            }
            role="presentation"
            data-tooltip={title}
          />
        )}
        {itemProps.item.text}
      </div>
    );
  }, [propsItemRender]);

  const splitButton = (
    <SplitButton
      data-testid={props.title || props.label}
      text={props.text}
      items={props.items}
      className={`${props.className || ""} ${props.imageUrl || props.icon ? props.text ? "icon-text-button" : "icon-button" : ""}`}
      buttonClass={props.buttonClassName}
      icon={props.icon}
      fillMode={props.fillMode || props.look ? props.fillMode || props.look : props.icon || props.imageUrl ? "flat" : "solid"}
      onButtonClick={(e) => onClick(e)}
      onItemClick={(e) => onClick(e)}
      disabled={props.disabled}
      popupSettings={props.popupSettings}
      startIcon={<SafeSVG src={props.imageUrl} />}
      themeColor={props.primary ? "primary" : "base"}
      ariaLabel={props.text || props.tooltip}
      itemRender={itemRender}
    />
  );

  if (props.tooltip) {
    return (
      <Tooltip
        anchorElement="target"
        position={props.position || "top"}
        openDelay={0}
        targetElement={ref.current}
      >
        <div
          ref={ref}
          className="split-button"
          title={props.tooltip}
        >
          {splitButton}
        </div>
      </Tooltip>
    );
  }

  return <div className="split-button">{splitButton}</div>;
};

SplitButtonComponent.propTypes = {
  text: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      icon: PropTypes.string,
      disableClick: PropTypes.bool,
    })
  ).isRequired,
  className: PropTypes.string,
  buttonClassName: PropTypes.string,
  icon: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  popupSettings: PropTypes.object,
  imageUrl: PropTypes.string,
  tooltip: PropTypes.string,
  position: PropTypes.string,
};

export default SplitButtonComponent;
